import React, { useState } from 'react';

// Add utility function to decode HTML entities
const decodeHtmlEntities = (str) => {
  if (!str) return str;
  
  // Create a temporary DOM element to decode HTML entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = str;
  return tempDiv.textContent || tempDiv.innerText || str;
};

const getStatusBadgeClass = (status) => {
  const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
  switch (status?.toLowerCase()) {
    case 'draft':
      return `${baseClasses} bg-gray-100 text-gray-700`;
    case 'released':
      return `${baseClasses} bg-blue-100 text-blue-700`;
    case 'not_released':
      return `${baseClasses} bg-yellow-100 text-yellow-700`;
    case 'approved_proposal_owner':
      return `${baseClasses} bg-green-100 text-green-700`;
    case 'rejected_proposal_owner':
      return `${baseClasses} bg-red-100 text-red-700`;
    case 'customer_rejected':
      return `${baseClasses} bg-red-100 text-red-700`;
    case 'customer_approved':
      return `${baseClasses} bg-emerald-100 text-emerald-700`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-700`;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatStatus = (status) => {
  if (!status) return '';
  
  // Map status codes to user-friendly labels
  const statusMap = {
    'draft': 'Draft',
    'released': 'Released',
    'not_released': 'Not Released',
    'approved_proposal_owner': 'Approved (Pending)',
    'rejected_proposal_owner': 'Rejected',
    'customer_rejected': 'Customer Rejected',
    'customer_approved': 'Customer Approved'
  };

  return statusMap[status] || status.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ProposalsTable = ({ proposals, loading, onRowClick, onEditDraft, onDeleteProposal, onReleaseProposal, onUpdateProposalTitle, onDuplicateProposal }) => {
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  const handleTitleClick = (e, proposal) => {
    e.stopPropagation();
    setEditingTitleId(proposal._id);
    setEditingTitle(decodeHtmlEntities(proposal.title) || '');
  };

  const handleTitleSave = async (proposalId) => {
    if (onUpdateProposalTitle) {
      try {
        await onUpdateProposalTitle(proposalId, editingTitle);
        setEditingTitleId(null);
        setEditingTitle('');
      } catch (error) {
        console.error('Failed to update title:', error);
      }
    }
  };

  const handleTitleCancel = () => {
    setEditingTitleId(null);
    setEditingTitle('');
  };

  const handleTitleKeyPress = (e, proposalId) => {
    if (e.key === 'Enter') {
      handleTitleSave(proposalId);
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!proposals?.length) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-gray-500">
        No proposals found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Title
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Client Price
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {proposals.map((proposal) => (
            <tr 
              key={proposal._id}
              onClick={() => onRowClick?.(proposal)}
              className="hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {proposal.proposalId}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {editingTitleId === proposal._id ? (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => handleTitleKeyPress(e, proposal._id)}
                      onBlur={() => handleTitleSave(proposal._id)}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      autoFocus
                    />
                    <button
                      onClick={() => handleTitleSave(proposal._id)}
                      className="text-green-600 hover:text-green-800"
                      title="Save"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleTitleCancel}
                      className="text-red-600 hover:text-red-800"
                      title="Cancel"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div 
                    className="flex items-center gap-2 group"
                    onClick={(e) => handleTitleClick(e, proposal)}
                  >
                    <span className="flex-1">{decodeHtmlEntities(proposal.title)}</span>
                    <svg 
                      className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {proposal.category?.name || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={getStatusBadgeClass(proposal.status)}>
                  {formatStatus(proposal.status)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatCurrency(proposal.clientPrice)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(proposal.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex gap-2">
                  {proposal.status === 'draft' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditDraft(proposal);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  )}
                  {proposal.status === 'approved_proposal_owner' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReleaseProposal(proposal);
                      }}
                      className="text-green-600 hover:text-green-800 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Release
                    </button>
                  )}
                  {/* Duplicate button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateProposal && onDuplicateProposal(proposal);
                    }}
                    className="text-purple-600 hover:text-purple-800 flex items-center gap-1"
                    title="Duplicate proposal"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    </svg>
                    Copy
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProposal(proposal);
                    }}
                    className="text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProposalsTable; 